import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import DCFMarkdown from '../../components/DCFMarkdown/DCFMarkdown';
import Notification from '../../components/Notification/Notification';
import PageTemplate from '../../components/PageTemplate/PageTemplate';
import Tag from '../../components/Tag/Tag';
import config from '../../config';
import content from '../../content';
import { useUiContext } from '../../context/UiContext';
import { useFormConfig } from '../../hooks/useFormConfig';
import { useFormData } from '../../hooks/useFormData';
import { Status } from '../../types';
import { getOverviewStatus } from '../../utils/config-utils';
import { DeclarationWrapper, SectionListWrapper } from './Wrappers';

export default function Overview() {
  const { isLoading } = useUiContext();
  const { formConfig, enabledSubsections } = useFormConfig();
  const { sections } = formConfig;

  const title = 'title' in formConfig ? String(formConfig.title) : undefined;
  const subTitle = 'subTitle' in formConfig ? String(formConfig.subTitle) : undefined;
  const showSections = formConfig.showSections === undefined ? true : formConfig.showSections;
  let index = 0;

  // fetch personal data
  const { data } = useFormData();

  const progressData = useMemo(
    () => getOverviewStatus(sections, enabledSubsections, data),
    [sections, enabledSubsections, data],
  );

  return (
    <>
      {isLoading ? (
        <PageTemplate title="">
          <h2 className="govuk-heading-m">Loading...</h2>
        </PageTemplate>
      ) : (
        <PageTemplate title={title}>
          <div data-testid="overview">
            {subTitle && <DCFMarkdown content={subTitle} type="subtitle" />}

            {data?.notification?.header && data?.notification?.content && (
              <Notification header={data.notification.header} content={data.notification.content} />
            )}

            <ol className="govuk-list" data-testid="overview-sectionList">
              {sections.map((section, sectionIndex) => (
                <SectionListWrapper
                  showSections={showSections}
                  section={section}
                  sectionIndex={sectionIndex}
                  key={section.slug}
                >
                  {section.subsections.map((subsection, subsectionIndex) => {
                    const status = progressData[subsection.slug] || Status.NotStarted;
                    index += 1;

                    const suffix =
                      subsection.pages.length > 1
                        ? 'summary'
                        : subsection.pages[0]?.name || 'summary';

                    const href =
                      status === Status.NotStarted
                        ? `/${section.slug}/${subsection.slug}`
                        : `/${section.slug}/${subsection.slug}/${suffix}`;

                    return (
                      <li key={subsection.slug} className="app-task-list__item">
                        <Link
                          to={href}
                          data-testid={`overview-subsectionList-${subsection.slug}Link`}
                        >
                          <span className="app-task-list__task-name">
                            {showSections
                              ? `${sectionIndex + 1}.${subsectionIndex + 1}`
                              : `${index}.`}
                            &nbsp;
                          </span>
                          {subsection.title}
                        </Link>
                        <Tag status={status} />
                      </li>
                    );
                  })}
                </SectionListWrapper>
              ))}
              {!!sections.length && (
                <DeclarationWrapper
                  showSections={showSections}
                  sections={sections}
                  titleOverride="Submit"
                >
                  <li className="app-task-list__item">
                    {Object.values(progressData).some(
                      (status: string) => status !== Status.Completed,
                    ) ? (
                      <span aria-disabled="true">{content.submitQuestionnaireLink}</span>
                    ) : (
                      <Link to={config.urls.summary} className="govuk-link">
                        {content.summaryLink}
                      </Link>
                    )}
                  </li>
                </DeclarationWrapper>
              )}
            </ol>
          </div>
        </PageTemplate>
      )}
    </>
  );
}
